import type { PageContent } from "../../components/PolicyPage";

const content: PageContent = {
  en: [
    {
      heading: "1. Presentation and acceptance",
      blocks: [
        {
          type: "p",
          text:
            "These Terms of Use govern access to and use of the Xlung App (the “App”), made available by XLUNG – Prestação de Serviços em Desenvolvimento de Software Ltda., enrolled with the CNPJ under No. 20.344.845/0001-01, with its registered office at Avenida Dom Luís, 1200, Torre I, sl. 805/806, Meireles – Fortaleza (CE), ZIP code 60.160-230. By creating an account, subscribing, or using the App, the user declares that they have read and understood these Terms. If the user does not agree, they must discontinue use.",
        },
      ],
    },
    {
      heading: "2. Purpose of the App",
      blocks: [
        {
          type: "p",
          text:
            "The Xlung App provides content, calculators, formulas, references, and educational resources related to mechanical ventilation and intensive care. The App is intended for healthcare professionals and students and serves an informational and educational purpose.",
        },
      ],
    },
    {
      heading: "3. Medical disclaimer and professional responsibility",
      blocks: [
        {
          type: "p",
          text:
            "The App does not replace clinical assessment, diagnosis, prescription, professional judgment, institutional protocols, or medical follow-up. Results and content must be interpreted by a qualified professional, taking into account the clinical context and up-to-date sources. The care decision and its consequences remain the responsibility of the professional who adopts it.",
        },
      ],
    },
    {
      heading: "4. Registration and account",
      blocks: [
        {
          type: "p",
          text:
            "Some features require registration. The user must provide true, complete, and up-to-date information, safeguard their credentials, and immediately report any suspected unauthorized access. The account is personal and non-transferable. XLUNG may apply security measures, suspend suspicious access, and request identity confirmation, in accordance with these Terms and applicable law.",
        },
      ],
    },
    {
      heading: "5. Subscriptions, payments, and renewal",
      blocks: [
        {
          type: "p",
          text:
            "Paid features may be offered by subscription through the Apple App Store, Google Play, or the payment processor indicated at the time of purchase. Price, billing period, taxes, trial period, automatic renewal, and cancellation method will be presented before purchase. Charges, refunds, and cancellations processed by the stores are also subject to their respective rules, without prejudice to the rights guaranteed by Brazilian law, including the Consumer Protection Code.",
        },
      ],
    },
    {
      heading: "6. License of use",
      blocks: [
        {
          type: "p",
          text:
            "XLUNG grants the user a limited, personal, non-exclusive, non-transferable, revocable, and non-sublicensable license to use the App for the duration of the account or subscription, solely for lawful purposes and in accordance with these Terms.",
        },
      ],
    },
    {
      heading: "7. Prohibited conduct",
      blocks: [
        {
          type: "p",
          text:
            "The following are prohibited: accessing another person’s account without authorization; circumventing security mechanisms; copying, modifying, distributing, selling, or improperly exploiting the App or its content; reverse engineering to the extent prohibited by law; introducing malicious code; interfering with the operation of the service; using the App for any unlawful purpose; or infringing the rights of third parties.",
        },
      ],
    },
    {
      heading: "8. Intellectual property",
      blocks: [
        {
          type: "p",
          text:
            "Texts, interfaces, illustrations, graphics, databases, trademarks, logos, features, technical materials, and other content of the App are protected by applicable law. Nothing herein transfers any intellectual property rights to the user. Unauthorized use may give rise to liability.",
        },
      ],
    },
    {
      heading: "9. Availability, updates, and changes",
      blocks: [
        {
          type: "p",
          text:
            "XLUNG may correct, update, modify, or discontinue features, including for technical, regulatory, or security reasons. Whenever a material change affects the user’s rights or the contracted use, appropriate notice will be provided and, where required, a period for acknowledgment or response.",
        },
      ],
    },
    {
      heading: "10. Suspension and termination",
      blocks: [
        {
          type: "p",
          text:
            "XLUNG may suspend or terminate accounts in the event of fraud, security risk, breach of these Terms, default, or legal obligation, observing proportionality, applicable law, and, where feasible, prior notice. The user may request the closure of their account through the channels indicated in the App.",
        },
      ],
    },
    {
      heading: "11. Privacy and personal data",
      blocks: [
        {
          type: "p",
          text:
            "The processing of personal data related to the App is described in the Xlung App Privacy Policy, which forms an integral part of these Terms. The Policy sets out the categories of data, purposes, legal bases, data sharing, retention, security, and the rights of data subjects.",
        },
      ],
    },
    {
      heading: "12. Links and third-party services",
      blocks: [
        {
          type: "p",
          text:
            "The App may integrate with or direct users to third-party services, such as app stores, payment platforms, and technology services. These third parties have their own terms and policies. XLUNG is responsible for the processing it carries out as a controller and takes reasonable measures to engage suppliers compatible with its obligations. These Terms are entered into between the user and XLUNG, and not with Apple or Google, which are not responsible for the App or its content; Apple is a third-party beneficiary of these Terms and may enforce them against the user.",
        },
      ],
    },
    {
      heading: "13. Limitation of liability",
      blocks: [
        {
          type: "p",
          text:
            "To the extent permitted by law, XLUNG does not guarantee uninterrupted availability or the complete absence of errors and is not liable for damages arising from use contrary to these Terms, external failures beyond its reasonable control, incorrect information provided by the user, or clinical decisions made without professional assessment. This clause does not exclude liabilities that cannot be waived by law.",
        },
      ],
    },
    {
      heading: "14. Communications",
      blocks: [
        {
          type: "p",
          text:
            "Operational, security, and contractual communications may be sent using the contact details provided. Promotional messages will be handled separately and will contain an opt-out mechanism where applicable.",
        },
      ],
    },
    {
      heading: "15. Changes to these Terms",
      blocks: [
        {
          type: "p",
          text:
            "XLUNG may update these Terms. The current version and the date of update will remain available in the App or on the website. Material changes will be communicated in a prominent manner. Consent will be requested again where required by law.",
        },
      ],
    },
    {
      heading: "16. Governing law and dispute resolution",
      blocks: [
        {
          type: "p",
          text:
            "These Terms are governed by the laws of the Federative Republic of Brazil. The consumer’s legally competent venue is preserved, including that of their domicile, in the cases provided for by law.",
        },
      ],
    },
    {
      heading: "17. Contact",
      blocks: [
        {
          type: "p",
          text:
            "Questions about these Terms may be sent to contact@xlung.net. Requests related to personal data must be sent to the channel indicated in the Privacy Policy.",
        },
      ],
    },
  ],
  pt: [
    {
      heading: "1. Apresentação e aceitação",
      blocks: [
        {
          type: "p",
          text:
            "Estes Termos de Uso regulam o acesso e a utilização do Xlung App (“Aplicativo”), disponibilizado por XLUNG – Prestação de Serviços em Desenvolvimento de Software Ltda., inscrita no CNPJ sob nº 20.344.845/0001-01, com sede na Avenida Dom Luís, 1200, Torre I, sl. 805/806, Meireles – Fortaleza (CE) CEP 60.160-230. Ao criar uma conta, contratar uma assinatura ou utilizar o Aplicativo, o usuário declara ter lido e compreendido estes Termos. Caso não concorde, deverá interromper o uso.",
        },
      ],
    },
    {
      heading: "2. Finalidade do Aplicativo",
      blocks: [
        {
          type: "p",
          text:
            "O Xlung App disponibiliza conteúdos, calculadoras, fórmulas, referências e recursos educacionais relacionados à ventilação mecânica e à terapia intensiva. O Aplicativo é destinado a profissionais e estudantes da área da saúde e possui finalidade informativa e educacional.",
        },
      ],
    },
    {
      heading: "3. Aviso médico e responsabilidade profissional",
      blocks: [
        {
          type: "p",
          text:
            "O Aplicativo não substitui avaliação clínica, diagnóstico, prescrição, julgamento profissional, protocolos institucionais ou acompanhamento médico. Resultados e conteúdos devem ser interpretados por profissional habilitado, considerando o contexto clínico e fontes atualizadas. A decisão assistencial e suas consequências permanecem sob responsabilidade do profissional que a adota.",
        },
      ],
    },
    {
      heading: "4. Cadastro e conta",
      blocks: [
        {
          type: "p",
          text:
            "Alguns recursos exigem cadastro. O usuário deve fornecer informações verdadeiras, completas e atualizadas, proteger suas credenciais e comunicar imediatamente suspeita de acesso não autorizado. A conta é pessoal e intransferível. A XLUNG poderá aplicar medidas de segurança, suspender acessos suspeitos e solicitar confirmação de identidade, observados estes Termos e a legislação.",
        },
      ],
    },
    {
      heading: "5. Assinaturas, pagamentos e renovação",
      blocks: [
        {
          type: "p",
          text:
            "Recursos pagos poderão ser oferecidos por assinatura por meio da Apple App Store, Google Play ou processador indicado no momento da contratação. Preço, periodicidade, tributos, período de teste, renovação automática e forma de cancelamento serão apresentados antes da compra. Cobranças, reembolsos e cancelamentos realizados pelas lojas também se sujeitam às respectivas regras, sem prejuízo dos direitos assegurados pela legislação brasileira, inclusive o Código de Defesa do Consumidor.",
        },
      ],
    },
    {
      heading: "6. Licença de uso",
      blocks: [
        {
          type: "p",
          text:
            "A XLUNG concede ao usuário licença limitada, pessoal, não exclusiva, intransferível, revogável e não sublicenciável para utilizar o Aplicativo durante a vigência da conta ou assinatura, exclusivamente para fins lícitos e conforme estes Termos.",
        },
      ],
    },
    {
      heading: "7. Condutas proibidas",
      blocks: [
        {
          type: "p",
          text:
            "É proibido: acessar conta de terceiros sem autorização; contornar mecanismos de segurança; copiar, modificar, distribuir, vender ou explorar indevidamente o Aplicativo ou seu conteúdo; praticar engenharia reversa na medida vedada por lei; introduzir código malicioso; interferir no funcionamento do serviço; usar o Aplicativo para finalidade ilícita; ou violar direitos de terceiros.",
        },
      ],
    },
    {
      heading: "8. Propriedade intelectual",
      blocks: [
        {
          type: "p",
          text:
            "Textos, interfaces, ilustrações, gráficos, bases, marcas, logotipos, funcionalidades, materiais técnicos e demais conteúdos do Aplicativo são protegidos pela legislação aplicável. Nenhuma disposição transfere ao usuário os direitos de propriedade intelectual. Usos não autorizados poderão gerar responsabilização.",
        },
      ],
    },
    {
      heading: "9. Disponibilidade, atualizações e alterações",
      blocks: [
        {
          type: "p",
          text:
            "A XLUNG poderá corrigir, atualizar, modificar ou descontinuar funcionalidades, inclusive por razões técnicas, regulatórias ou de segurança. Sempre que uma alteração material afetar direitos do usuário ou a utilização contratada, será fornecida comunicação adequada e, quando exigido, prazo para ciência ou manifestação.",
        },
      ],
    },
    {
      heading: "10. Suspensão e encerramento",
      blocks: [
        {
          type: "p",
          text:
            "A XLUNG poderá suspender ou encerrar contas em caso de fraude, risco de segurança, violação destes Termos, inadimplemento ou obrigação legal, observadas a proporcionalidade, a legislação e, quando viável, comunicação prévia. O usuário poderá solicitar o encerramento da conta pelos canais indicados no Aplicativo.",
        },
      ],
    },
    {
      heading: "11. Privacidade e dados pessoais",
      blocks: [
        {
          type: "p",
          text:
            "O tratamento de dados pessoais relacionados ao Aplicativo é descrito na Política de Privacidade do Xlung App, que integra estes Termos. A Política informa categorias de dados, finalidades, bases legais, compartilhamentos, retenção, segurança e direitos dos titulares.",
        },
      ],
    },
    {
      heading: "12. Links e serviços de terceiros",
      blocks: [
        {
          type: "p",
          text:
            "O Aplicativo poderá integrar ou direcionar a serviços de terceiros, como lojas de aplicativos, plataformas de pagamento e serviços de tecnologia. Esses terceiros possuem termos e políticas próprios. A XLUNG responde pelos tratamentos que realizar como controladora e adota medidas razoáveis para contratar fornecedores compatíveis com suas obrigações. Estes Termos são celebrados entre o usuário e a XLUNG, e não com a Apple ou o Google, que não são responsáveis pelo Aplicativo ou por seu conteúdo; a Apple é beneficiária terceira destes Termos e poderá exigir seu cumprimento perante o usuário.",
        },
      ],
    },
    {
      heading: "13. Limitação de responsabilidade",
      blocks: [
        {
          type: "p",
          text:
            "Na extensão permitida por lei, a XLUNG não garante disponibilidade ininterrupta ou ausência absoluta de erros e não responde por danos decorrentes de uso contrário a estes Termos, falhas externas fora de seu controle razoável, informações incorretas fornecidas pelo usuário ou decisões clínicas tomadas sem avaliação profissional. Esta cláusula não exclui responsabilidades que não possam ser afastadas por lei.",
        },
      ],
    },
    {
      heading: "14. Comunicações",
      blocks: [
        {
          type: "p",
          text:
            "Comunicações operacionais, de segurança e contratuais poderão ser enviadas pelos dados de contato informados. Mensagens promocionais serão tratadas separadamente e conterão mecanismo de descadastramento, quando aplicável.",
        },
      ],
    },
    {
      heading: "15. Alterações destes Termos",
      blocks: [
        {
          type: "p",
          text:
            "A XLUNG poderá atualizar estes Termos. A versão vigente e a data de atualização permanecerão disponíveis no Aplicativo ou no site. Alterações relevantes serão comunicadas de forma destacada. O consentimento será solicitado novamente quando exigido pela legislação.",
        },
      ],
    },
    {
      heading: "16. Lei aplicável e solução de controvérsias",
      blocks: [
        {
          type: "p",
          text:
            "Estes Termos são regidos pelas leis da República Federativa do Brasil. Fica preservado ao consumidor o foro legalmente competente, inclusive o de seu domicílio, nos casos previstos em lei.",
        },
      ],
    },
    {
      heading: "17. Contato",
      blocks: [
        {
          type: "p",
          text:
            "Dúvidas sobre estes Termos poderão ser encaminhadas para contact@xlung.net. Solicitações relacionadas a dados pessoais deverão ser enviadas ao canal indicado na Política de Privacidade.",
        },
      ],
    },
  ],
};

export default content;
